<template>
  <div v-if="evidence.length" class="audit-evidence">
    <strong class="audit-evidence-label">Evidence:</strong>
    <span class="audit-evidence-icons">
      <button
        v-if="hasImages"
        type="button"
        class="audit-evidence-icon"
        title="View Images"
        @click.stop="openGallery('image')">
        <i class="fa fa-file-image-o"></i>
      </button>
      <button
        v-if="hasVideos"
        type="button"
        class="audit-evidence-icon"
        title="View Videos"
        @click.stop="openGallery('video')">
        <i class="fa fa-file-video-o"></i>
      </button>
    </span>

    <b-modal
      v-model="visible"
      centered
      hide-footer
      dialog-class="audit-evidence-modal-dialog"
      body-class="audit-evidence-modal-body"
      header-bg-variant="dark"
      header-text-variant="light"
      body-bg-variant="dark"
      body-text-variant="light"
      @hidden="resetViewer">
      <template #modal-title>
        <span v-if="selectedEvidence">
          {{ selectedEvidence.original_name }}
          <small class="audit-evidence-title-size">({{ formatSize(selectedEvidence.size_bytes) }})</small>
        </span>
        <span v-else>{{ galleryType === 'image' ? 'Images' : 'Videos' }} Evidence</span>
      </template>

      <div v-if="selectedEvidence" class="audit-evidence-full">
        <button type="button" class="btn btn-outline-light btn-sm audit-evidence-back" @click="showOverview">
          <i class="fa fa-arrow-left"></i>
          Back to gallery
        </button>
        <div
          ref="imageViewport"
          class="audit-evidence-full-content"
          :class="{
            'audit-evidence-image-content': selectedEvidence.media_type === 'image',
            'audit-evidence-image-pannable': selectedEvidence.media_type === 'image' && imageZoom > 1,
            'audit-evidence-image-dragging': imageDragging
          }"
          @mousedown="startImageDrag"
          @wheel="zoomImage">
          <img
            v-if="selectedEvidence.media_type === 'image'"
            ref="activeImage"
            :src="selectedEvidence.url"
            :alt="selectedEvidence.original_name"
            class="audit-evidence-full-image"
            draggable="false"
            :style="imageTransform">
          <video
            v-else
            :key="'evidence-video-' + selectedEvidence.id"
            ref="activeVideo"
            :src="selectedEvidence.url"
            class="audit-evidence-full-video"
            controls
            autoplay
            playsinline
            preload="metadata">
            Your browser cannot play this video format.
          </video>
        </div>
        <small v-if="selectedEvidence.media_type === 'image'" class="audit-evidence-zoom">
          Mouse wheel to zoom. Drag with the left mouse button to pan. ({{ Math.round(imageZoom * 100) }}%)
        </small>
      </div>

      <div v-else class="audit-evidence-grid">
        <div
          v-for="item in filteredEvidence"
          :key="'evidence-preview-' + item.id"
          class="audit-evidence-preview-wrapper">
          <button
            type="button"
            class="audit-evidence-preview"
            @click="selectEvidence(item)">
            <img
              v-if="item.media_type === 'image'"
              :src="item.url"
              :alt="item.original_name"
              loading="lazy">
            <span v-else class="audit-evidence-video-preview">
              <i class="fa fa-play-circle-o"></i>
            </span>
            <span class="audit-evidence-filename">{{ item.original_name }}</span>
            <small class="audit-evidence-size">{{ formatSize(item.size_bytes) }}</small>
          </button>
          <button
            v-if="editable"
            type="button"
            class="btn btn-danger btn-sm audit-evidence-delete"
            title="Delete evidence"
            @click.stop="confirmDelete(item)">
            <i class="fa fa-trash-o"></i>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import iziToast from 'izitoast'

export default {
  name: 'AuditLogEvidence',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    targetType: {
      type: String,
      default: 'entry'
    },
    targetId: {
      type: [Number, String],
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: false,
      galleryType: null,
      selectedEvidence: null,
      imageZoom: 1,
      imageOffset: {
        x: 0,
        y: 0
      },
      imageDragging: false,
      imageDragStart: {
        x: 0,
        y: 0,
        offsetX: 0,
        offsetY: 0
      }
    }
  },
  computed: {
    evidence () {
      return Array.isArray(this.items) ? this.items : []
    },
    hasImages () {
      return this.evidence.some(i => i.media_type === 'image')
    },
    hasVideos () {
      return this.evidence.some(i => i.media_type === 'video')
    },
    filteredEvidence () {
      if (!this.galleryType) return []
      return this.evidence.filter(i => i.media_type === this.galleryType)
    },
    imageTransform () {
      return {
        transform: `translate(${this.imageOffset.x}px, ${this.imageOffset.y}px) scale(${this.imageZoom})`
      }
    }
  },
  beforeDestroy () {
    this.stopImageDrag()
  },
  methods: {
    formatSize (size) {
      size = Number(size) || 0
      if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(1)} MB`
      }
      return `${Math.max(1, Math.round(size / 1024))} KB`
    },
    openGallery (type) {
      this.galleryType = type
      this.selectedEvidence = null
      this.visible = true
    },
    selectEvidence (item) {
      this.selectedEvidence = item
      this.resetImagePosition()
      if (item.media_type === 'video') {
        this.$nextTick(() => {
          const video = this.$refs.activeVideo
          if (video && video.play) {
            video.play().catch(() => {})
          }
        })
      }
    },
    showOverview () {
      this.pauseVideo()
      this.selectedEvidence = null
      this.resetImagePosition()
    },
    pauseVideo () {
      const video = this.$refs.activeVideo
      if (video && video.pause) {
        video.pause()
      }
    },
    resetViewer () {
      this.pauseVideo()
      this.selectedEvidence = null
      this.galleryType = null
      this.resetImagePosition()
    },
    zoomImage (event) {
      if (!this.selectedEvidence || this.selectedEvidence.media_type !== 'image') {
        return
      }
      event.preventDefault()
      const direction = event.deltaY < 0 ? 0.2 : -0.2
      this.imageZoom = Math.min(5, Math.max(0.4, Number((this.imageZoom + direction).toFixed(2))))
      this.$nextTick(this.clampImageOffset)
    },
    resetImagePosition () {
      this.imageZoom = 1
      this.imageOffset = { x: 0, y: 0 }
      this.stopImageDrag()
    },
    startImageDrag (event) {
      if (
        event.button !== 0 ||
        !this.selectedEvidence ||
        this.selectedEvidence.media_type !== 'image' ||
        this.imageZoom <= 1
      ) {
        return
      }
      event.preventDefault()
      this.imageDragging = true
      this.imageDragStart = {
        x: event.clientX,
        y: event.clientY,
        offsetX: this.imageOffset.x,
        offsetY: this.imageOffset.y
      }
      window.addEventListener('mousemove', this.moveImage)
      window.addEventListener('mouseup', this.stopImageDrag)
    },
    moveImage (event) {
      if (!this.imageDragging) {
        return
      }
      this.imageOffset = {
        x: this.imageDragStart.offsetX + event.clientX - this.imageDragStart.x,
        y: this.imageDragStart.offsetY + event.clientY - this.imageDragStart.y
      }
      this.clampImageOffset()
    },
    stopImageDrag () {
      this.imageDragging = false
      window.removeEventListener('mousemove', this.moveImage)
      window.removeEventListener('mouseup', this.stopImageDrag)
    },
    clampImageOffset () {
      const viewport = this.$refs.imageViewport
      const image = this.$refs.activeImage
      if (!viewport || !image) {
        return
      }
      const maxX = Math.max(0, (image.offsetWidth * this.imageZoom - viewport.clientWidth) / 2)
      const maxY = Math.max(0, (image.offsetHeight * this.imageZoom - viewport.clientHeight) / 2)
      this.imageOffset = {
        x: Math.min(maxX, Math.max(-maxX, this.imageOffset.x)),
        y: Math.min(maxY, Math.max(-maxY, this.imageOffset.y))
      }
    },
    confirmDelete (item) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to delete "${item.original_name}"?`, {
        title: 'Delete Evidence',
        size: 'lg',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        bodyClass: 'text-nowrap',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
        .then(value => {
          if (value) {
            this.deleteEvidence(item)
          }
        })
    },
    async deleteEvidence (item) {
      try {
        const response = await axios.delete(`/auditlog/evidence/${this.targetType}/${item.id}`)
        this.$emit('evidence-changed', response.data.evidence)
        iziToast.success({
          title: 'My Activity Log',
          message: 'Evidence deleted successfully.'
        })
        if (!response.data.evidence || !response.data.evidence.some(i => i.media_type === this.galleryType)) {
          this.visible = false
        }
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Could not delete the evidence.'
        iziToast.error({
          title: 'My Activity Log',
          message: message
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-evidence {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 5px;
}

.audit-evidence-label {
  flex: 0 0 auto;
}

.audit-evidence-icons {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 5px;
}

.audit-evidence-icon {
  display: inline-flex;
  width: 30px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(99, 194, 222, 0.5);
  align-items: center;
  justify-content: center;
  background: rgba(32, 168, 216, 0.1);
  color: #63c2de;
  font-size: 1rem;
}

.audit-evidence-icon:hover {
  border-color: #63c2de;
  background: rgba(32, 168, 216, 0.22);
}

.audit-evidence-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.audit-evidence-preview-wrapper {
  position: relative;
}

.audit-evidence-preview {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #20262c;
  color: #d8dee4;
  overflow: hidden;
  text-align: left;
}

.audit-evidence-preview:hover {
  border-color: #63c2de;
}

.audit-evidence-delete {
  position: absolute;
  top: 5px;
  right: 5px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.audit-evidence-delete:hover {
  opacity: 1;
}

.audit-evidence-preview img,
.audit-evidence-video-preview {
  display: flex;
  width: 100%;
  height: 150px;
  object-fit: contain;
  align-items: center;
  justify-content: center;
  background: #11161a;
}

.audit-evidence-video-preview {
  color: #63c2de;
  font-size: 3rem;
}

.audit-evidence-filename {
  display: block;
  padding: 8px 10px 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audit-evidence-size {
  display: block;
  padding: 0 10px 8px;
  color: rgba(255, 255, 255, 0.58);
}

.audit-evidence-title-size {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78em;
}

.audit-evidence-full {
  position: relative;
  min-height: 280px;
  padding-top: 42px;
  text-align: center;
}

.audit-evidence-back {
  position: absolute;
  top: 0;
  left: 0;
}

.audit-evidence-full-image,
.audit-evidence-full-video {
  max-width: 100%;
  max-height: calc(100vh - 220px);
}

.audit-evidence-image-content {
  display: flex;
  height: calc(100vh - 210px);
  min-height: 280px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
}

.audit-evidence-image-pannable {
  cursor: grab;
}

.audit-evidence-image-dragging {
  cursor: grabbing;
}

.audit-evidence-full-image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  transform-origin: center center;
  transition: transform 0.08s linear;
  user-select: none;
}

.audit-evidence-zoom {
  display: block;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.58);
  text-align: center;
}

.audit-evidence-full-video {
  width: 100%;
  background: #000;
}
</style>

<style lang="scss">
.audit-evidence-modal-dialog {
  width: calc(100vw - 80px);
  max-width: 1500px;
  max-height: calc(100vh - 24px);
  margin: 12px auto;
}

@media (max-width: 575.98px) {
  .audit-evidence-modal-dialog {
    width: calc(100vw - 24px);
  }
}

.audit-evidence-modal-dialog .modal-content {
  max-height: calc(100vh - 24px);
}

.audit-evidence-modal-body {
  max-height: calc(100vh - 82px);
  overflow: auto;
}
</style>
